import { CrewAccordion } from 'components/elements/crewAccordion/crewAccordion'
import { FC, memo, useCallback } from 'react'
import { FavoriteProjectList } from './components/favoriteProjectList/favoriteProjectList'
import {
  FavoriteProjectMenuActionType,
  FavoriteProjectContextMenu,
} from './components/favoriteProjectContextMenu/favoriteProjectContextMenu'
import { useTranslation } from '@crew/modules/i18n'
import { useUpdateFavoriteDisplayOrderTypeMutation } from '@crew/apis/favorite/favoriteApis'
import { FavoriteDisplayOrderType } from '@crew/enums/domain'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useLoadUserSettings } from '@crew/states'
import { useModal } from 'components/layouts/modal/useModal'
import { useSystemPermissions } from '@crew/hooks'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { getDefaultTabValue } from '@crew/utils/enum'
import { ProjectDetailTabs } from 'enums/app'
import { ProjectEntryDialog } from 'features/project/components/projectEntryDialog/projectEntryDialog'
import Add from '~icons/material-symbols/add'

// お気に入りプロジェクトを表示するパネル
export const FavoriteProjectPanel: FC = memo((props) => {
  const { t } = useTranslation()
  const [showApiErrors] = useShowApiErrors()
  const { navigate } = useCrewNavigate()

  // プロジェクト登録ダイアログの状態
  const [
    isProjectEntryDialogOpen,
    openProjectEntryDialog,
    closeProjectEntryDialog,
  ] = useModal()

  // プロジェクト作成権限
  const { hasSysProjectCreateDeletePermission } = useSystemPermissions()

  // お気に入り表示順設定変更API
  const [updateFavoriteDisplayOrderTypeMutation] =
    useUpdateFavoriteDisplayOrderTypeMutation()

  // ユーザー設定再取得
  const [loadUserSettings] = useLoadUserSettings()

  const handleMenuItemClick = useCallback(
    async (action: FavoriteProjectMenuActionType) => {
      try {
        // お気に入り表示順種類設定変更APIを実行
        await updateFavoriteDisplayOrderTypeMutation({
          displayOrderType:
            action === FavoriteProjectMenuActionType.nameOrder
              ? FavoriteDisplayOrderType.Name
              : FavoriteDisplayOrderType.Activity,
        }).unwrap()

        // Sliceにも反映
        loadUserSettings()
      } catch (error) {
        showApiErrors(error)
      }
    },
    [loadUserSettings, showApiErrors, updateFavoriteDisplayOrderTypeMutation]
  )

  // プロジェクト登録完了時の処理
  const handleProjectRegistered = useCallback(
    (projectId: string) => {
      // プロジェクト詳細画面に遷移
      navigate(
        `/projects/${projectId}/${getDefaultTabValue(ProjectDetailTabs)}`
      )
      // ダイアログを閉じる
      closeProjectEntryDialog()
    },
    [closeProjectEntryDialog, navigate]
  )

  const renderToolIcon = useCallback(
    () => (
      <>
        {/* プロジェクト作成権限がある場合のみ＋ボタンを表示 */}
        {hasSysProjectCreateDeletePermission && (
          <Add width={20} height={20} onClick={openProjectEntryDialog} />
        )}
        <FavoriteProjectContextMenu onMenuItemClick={handleMenuItemClick} />
      </>
    ),
    [
      hasSysProjectCreateDeletePermission,
      openProjectEntryDialog,
      handleMenuItemClick,
    ]
  )

  return (
    <>
      <CrewAccordion
        id="favorite-project-panel"
        title={t('label.project')}
        content={<FavoriteProjectList />}
        renderToolIcon={renderToolIcon}
      />

      {/* プロジェクト登録ダイアログ */}
      <ProjectEntryDialog
        isEditMode={false}
        title={t('label.projectAddTitle')}
        isOpen={isProjectEntryDialogOpen}
        onClose={closeProjectEntryDialog}
        onSubmit={handleProjectRegistered}
      />
    </>
  )
})
